import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

export const themeManagementSchema = z.object({
  //Store Name: Required, 3-50 characters, only letters, numbers, &, -
  storeName: z
    .string()
    .min(2, "Store name must be at least 2 characters long.")
    .max(50, "Store name cannot exceed 50 characters.")
    .regex(/^[a-zA-Z0-9&-\s]+$/, "Only letters, numbers, &, and - are allowed."),

  //Store Description: Required, 10-500 characters
  storeDescription: z
    .string()
    .min(10, "Store description must be at least 10 characters.")
    .max(500, "Store description cannot exceed 500 characters."),

  //Phone Numbers: At least one telephone number, contain only digits, must be valid format, 9-12 digits
  phoneNumbers: z
    .array(
      z.object({
        countryCode: z.string().min(3, "Country code is required"),
        number: z
          .string()
          .regex(/^\d+$/, "Phone number must contain only numbers")
          .min(9, "Phone number must be at least 9 digits")
          .max(12, "Phone number cannot exceed 12 digits"),
      })
    )
    .min(1, "At least one phone number is required"),

  //Email Addresses: At least one, must be valid email format
  emails: z
    .array(
      z.object({
        email: z.string().email("Enter a valid email address"),
      })
    )
    .min(1, "At least one email address is required."),

  //Store Logo: Optional, but if uploaded, must be PNG, JPG, SVG, max 2MB
  storeLogo: z
    .instanceof(File)
    .refine((file) => ["image/png", "image/jpeg"].includes(file.type), "Only PNG or JPEG allowed.")
    .refine((file) => file.size <= 2 * 1024 * 1024, "File size must be under 2MB.")
    .optional(),

  //Opening Hours: At least one schedule, valid time range
  schedules: z
    .array(
      z.object({
        dayStart: z.string().min(1, "Start day is required"),
        dayEnd: z.string().min(1, "End day is required"),
        openTime: z.string().min(1, "Opening time is required"),
        closeTime: z.string().min(1, "Closing time is required"),
      })
    )
    .min(1, "At least one schedule is required") // ✅ Ensure at least one schedule exists
    .superRefine((scheduleArray, ctx) => {
      scheduleArray.forEach((schedule, index) => {
        if (schedule.openTime && schedule.closeTime && schedule.closeTime <= schedule.openTime) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [index, "closeTime"],
            message: "Closing time must be after opening time",
          });
        }
      });
    }),

  //Theme Name: Required, 2-25 characters
  themes: z
    .array(
      z.object({
        name: z
          .string()
          .min(2, "Theme name must be at least 2 characters long")
          .max(25, "Theme name must not exceed 25 characters"),
        imageUrl: z
          .union([
            z.string().url("Invalid image URL"),
            z.literal(""), // Allows empty values
          ])
          .optional(), // Allows empty imageUrl
        isActive: z.boolean(),
      })
    )
    .nonempty("At least one theme is required"),

  // Social Media Links: Optional, but must be valid URLs
  socialMedia: z.object({
    facebook: z
      .string()
      .trim()
      .optional()
      .refine((val) => !val || /^https?:\/\/(www\.)?facebook\.com\/[A-Za-z0-9._-]+$/.test(val), {
        message: "Invalid Facebook URL. Use https://facebook.com/yourpage",
      }),

    instagram: z
      .string()
      .trim()
      .optional()
      .refine((val) => !val || /^https?:\/\/(www\.)?instagram\.com\/[A-Za-z0-9._-]+$/.test(val), {
        message: "Invalid Instagram URL. Use https://instagram.com/yourprofile",
      }),

    tiktok: z
      .string()
      .trim()
      .optional()
      .refine((val) => !val || /^https?:\/\/(www\.)?tiktok\.com\/@[A-Za-z0-9._-]+$/.test(val), {
        message: "Invalid TikTok URL. Use https://tiktok.com/@yourusername",
      }),
  }),
});
